"use client";
import React, { useState, useEffect, useRef } from 'react';
import { useMediaQuery, useTheme, IconButton, Box, Stack, styled, Button } from '@mui/material';
import { RiFilterLine } from 'react-icons/ri';
import { FiChevronLeft, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import FiltersPopover from './FiltersPopover';
import SortPopover from './SortPopover';
import { useDeals } from 'app/providers/deals-provider';
import { useAuth } from 'app/providers/auth-provider';
import { usePathname } from 'next/navigation';
import AsinLookup from './AsinLookup';

// Styled components following the compact design
const CompactIconButton = styled(IconButton)(({ theme }) => ({
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    border: '1px solid var(--theme-color, #436B78)',
    color: 'var(--theme-color, #436B78)',
    backgroundColor: 'transparent',
    '&:hover': {
        backgroundColor: 'rgba(67, 107, 120, 0.1)',
    }
}));

const CategoryButton = styled(Button)(({ theme, isActive }) => ({
    height: '32px',
    borderRadius: '8px',
    padding: '6px 12px',
    minWidth: 'auto',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    fontSize: '13px',
    fontWeight: 400,
    backgroundColor: isActive ? 'var(--theme-dark, #2B4D59)' : 'transparent',
    color: isActive ? '#FFF' : 'var(--theme-dark, #2B4D59)',
    border: '1px solid',
    borderColor: isActive ? 'var(--theme-color, #436B78)' : 'var(--theme-dark, #2B4D59)',
    '&:hover': {
        backgroundColor: isActive ? 'var(--theme-dark, #2B4D59)' : 'rgba(67, 107, 120, 0.1)',
    },
    '&:disabled': {
        opacity: 0.3,
        cursor: 'not-allowed',
    }
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: '1px solid var(--theme-color, #436B78)',
    color: 'var(--theme-color, #436B78)',
    backgroundColor: 'transparent',
    padding: '4px',
    '&:hover': {
        backgroundColor: 'rgba(67, 107, 120, 0.1)',
    },
    '&:disabled': {
        opacity: 0.3,
        cursor: 'not-allowed',
    }
}));

const ScrollContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    flex: 1,
    padding: '0 8px',
    '&::-webkit-scrollbar': {
        display: 'none',
    }
}));

const CompactSortButton = styled(Button)(({ theme }) => ({
    color: 'var(--theme-dark, #2B4D59)',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    height: '36px',
    padding: '8px 12px',
    borderRadius: '8px',
    minWidth: 'auto',
    '&:hover': {
        backgroundColor: 'rgba(43, 77, 89, 0.04)'
    },
    '& .MuiButton-endIcon': {
        marginLeft: '4px',
    }
}));

const DealsHeader = () => {
    const pathname = usePathname();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [filtersAnchorEl, setFiltersAnchorEl] = useState(null);
    const [sortAnchorEl, setSortAnchorEl] = useState(null);
    const scrollContainerRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    
    const {
        collection,
        DEFAULT_COLLECTION,
        collectionCategories,
        selectedCollectionCategory,
        setSelectedCollectionCategory,
        isDealLoading,
        filters
    } = useDeals();
    
    const filtersOpen = Boolean(filtersAnchorEl);
    const sortOpen = Boolean(sortAnchorEl);
    const authProvider = useAuth();
    const [user, setUser] = useState(null);

    const isFeatureCategory = collection.value && collection.value === DEFAULT_COLLECTION.value;
    const shouldShowBreadcrumb = collection && collection !== DEFAULT_COLLECTION.value;

    const handleFiltersOpen = (event) => {
        setFiltersAnchorEl(event.currentTarget);
    };

    const handleFiltersClose = () => {
        setFiltersAnchorEl(null);
    };

    const handleSortOpen = (event) => {
        setSortAnchorEl(event.currentTarget);
    };

    const handleSortClose = () => {
        setSortAnchorEl(null);
    };

    const checkOverflow = () => {
        if (scrollContainerRef.current) {
            const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollWidth > clientWidth + scrollLeft);
        }
    };

    const scroll = (direction) => {
        if (scrollContainerRef.current) {
            const { scrollLeft, clientWidth } = scrollContainerRef.current;
            const scrollAmount = clientWidth / 2;
            const newScrollLeft = direction === "left"
                ? scrollLeft - scrollAmount
                : scrollLeft + scrollAmount;

            scrollContainerRef.current.scrollTo({
                left: newScrollLeft,
                behavior: "smooth"
            });
        }
    };

    useEffect(() => {
        if (authProvider && (authProvider.user && authProvider.user?.email)) {
            setUser(authProvider.user);
        }
    }, [authProvider]);

    useEffect(() => {
        checkOverflow();
        const handleResize = () => checkOverflow();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [selectedCollectionCategory, collectionCategories]);

    return (
        <>
            <div className="deals-header" style={{ marginTop: '20px' }}>
                {/* <div className="deals-header__top" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginBottom: '16px'
                }}>
                    <h1 className="deals-header__title" style={{ fontSize: '35px', margin: 0 }}>Deals</h1>
                </div> */}

                <Box sx={{marginBottom: '20px'}} id="asin-lookup-button-tour">
                {(['/deals/creator', '/deals/creator/'].includes(pathname) || pathname.startsWith('/deals/creator/')) && <AsinLookup/>}
                </Box>

                {/* Compact Controls Row */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    minHeight: '36px'
                }}>
                    {/* Filter Button - Left */}
                    <CompactIconButton
                        onClick={handleFiltersOpen}
                        aria-label="Filters"
                    >
                        <RiFilterLine size={18} />
                    </CompactIconButton>

                    {/* Category Chips - Center */}
                    {collectionCategories && collectionCategories.length > 0 && (
                        <>
                            {/* Desktop: Show arrows for overflow */}
                            {!isMobile && showLeftArrow && (
                                <NavigationButton
                                    onClick={() => scroll("left")}
                                    disabled={isDealLoading}
                                    size="small"
                                >
                                    <FiChevronLeft size={16} />
                                </NavigationButton>
                            )}

                            <ScrollContainer
                                ref={scrollContainerRef}
                                onScroll={checkOverflow}
                                sx={{
                                    maxWidth: isMobile ? 'calc(100% - 80px)' : 'calc(100% - 200px)'
                                }}
                            >
                                <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'nowrap' }}>
                                    {collectionCategories.map((category, index) => {
                                        const isActive = category.value === selectedCollectionCategory?.value;
                                        return (
                                            <CategoryButton
                                                key={index}
                                                onClick={() => setSelectedCollectionCategory(prev => 
                                                    prev?.value === category.value ? null : category
                                                )}
                                                disabled={isDealLoading}
                                                isActive={isActive}
                                            >
                                                {category.label}
                                            </CategoryButton>
                                        );
                                    })}
                                </Stack>
                            </ScrollContainer>

                            {/* Desktop: Show arrows for overflow */}
                            {!isMobile && showRightArrow && (
                                <NavigationButton
                                    onClick={() => scroll("right")}
                                    disabled={isDealLoading}
                                    size="small"
                                >
                                    <FiChevronRight size={16} />
                                </NavigationButton>
                            )}
                        </>
                    )}

                    {/* Sort Button - Right (Desktop only) */}
                    {!isMobile && !isFeatureCategory && (
                        <CompactSortButton
                            onClick={handleSortOpen}
                            endIcon={<FiChevronDown size={16} />}
                        >
                            Sort
                        </CompactSortButton>
                    )}
                </Box>

                {/* Breadcrumb */}
                {/* {(shouldShowBreadcrumb && !isMobile) && (
                    <div className="deals-header__breadcrumb" style={{ 
                        marginTop: '12px', 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#666', 
                        textAlign: 'left' 
                    }}>
                        <span style={{ color: 'var(--theme-dark)' }}>Home /</span> 
                        <span style={{ color: 'var(--second-color)' }}>
                            {collection.label === 'Featured Products' ? (user?.creatorId ? 'Your Products' : 'All') : collection.label}
                        </span>
                    </div>
                )} */}
            </div>

            {/* Filters Popover */}
            <FiltersPopover
                open={filtersOpen}
                anchorEl={filtersAnchorEl}
                onClose={handleFiltersClose}
                hideSort={isFeatureCategory}
            />

            {/* Sort Popover for Desktop */}
            {!isMobile && (
                <SortPopover
                    open={sortOpen}
                    anchorEl={sortAnchorEl}
                    onClose={handleSortClose}
                />
            )}
        </>
    );
};

export default DealsHeader; 